import React, { useState, useRef, useEffect } from 'react';
import './index.css';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';

interface Slide {
    id: string;
    product: string;
    keyMessage: string;
    name: string;
    starttime: number;
    endtime: number;
}

interface Scenario {
    id: string;
    scenarioName: string;
    duration: number;
    slides: Slide[];
}

interface VideoPlayerProps {
    videoUrl: string;
    scenario: Scenario;
    doctorId: string;
}


const VideoPlayer: React.FC<VideoPlayerProps> = ({ videoUrl, scenario, doctorId }) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const [isPlaying, setIsPlaying] = useState(false);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [currentSlide, setCurrentSlide] = useState<Slide | null>(null);
    const [slideStats, setSlideStats] = useState<Record<string, number>>({});
    const [isMuted, setIsMuted] = useState(false);
    const [showControls, setShowControls] = useState(true);
    const [fadeOut, setFadeOut] = useState(false);
    const controlsTimeout = useRef<NodeJS.Timeout | null>(null);
    const [isSeeking, setIsSeeking] = useState(false);
    const progressBarRef = useRef<HTMLDivElement>(null);

// Сбросить таймер и запустить плавное скрытие через 5 сек.
    const handleUserActivity = () => {
        setShowControls(true);
        setFadeOut(false);
        if (controlsTimeout.current) clearTimeout(controlsTimeout.current);
        controlsTimeout.current = setTimeout(() => setFadeOut(true), 5000);
    };

// Клик по пустой области: если панель видна — скрыть сразу, если скрыта — показать сразу
    const handleContainerClick = (e: React.MouseEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget) {
            if (showControls) {
                setShowControls(false);   // мгновенно скрыть
                setFadeOut(false);
                if (controlsTimeout.current) clearTimeout(controlsTimeout.current);
            } else {
                setShowControls(true);    // мгновенно показать
                setFadeOut(false);
                handleUserActivity();
            }
        }
    };

    useEffect(() => {
        handleUserActivity();
        return () => {
            if (controlsTimeout.current) clearTimeout(controlsTimeout.current);
        };
    }, []);


    const statsToShow = scenario.slides.map(slide => ({
        doctor_id: doctorId,
        slide_id: slide.id,
        product_name: slide.product,
        slide_name: slide.name,
        timeOnSlide: slideStats[slide.id] || 0,
    }));

    const totalTime = statsToShow.reduce((sum, stat) => sum + stat.timeOnSlide, 0);

    // Инициализация статистики
    useEffect(() => {
        const initialStats: Record<string, number> = {};
        scenario.slides.forEach(slide => {
            initialStats[slide.id] = 0;
        });
        setSlideStats(initialStats);
    }, [scenario]);

    // Определение текущего слайда
    useEffect(() => {
        if (scenario.slides.length === 0) return;
        const slide = scenario.slides.find(s => currentTime >= s.starttime && currentTime < s.endtime);
        setCurrentSlide(slide || null);
    }, [currentTime, scenario]);

    // Обновление статистики каждую секунду
    useEffect(() => {
        const interval = setInterval(() => {
            if (
                !videoRef.current ||
                videoRef.current.currentTime === 0 ||
                videoRef.current.ended ||
                !isPlaying // <-- новая проверка
            ) {
                return;
            }
            if (currentSlide) {
                setSlideStats(prev => ({
                    ...prev,
                    [currentSlide.id]: (prev[currentSlide.id] || 0) + 1
                }));
                // отправка статистики...
                console.log(`Статистика: doctorId=${doctorId}, slideId=${currentSlide.id}, timeSpent=${slideStats[currentSlide.id]}`);
            }
        }, 1000);

        return () => clearInterval(interval);
    }, [currentSlide, slideStats, isPlaying]);

    // Обновление текущего времени видео
    const handleTimeUpdate = () => {
        if (videoRef.current) {
            setCurrentTime(Math.floor(videoRef.current.currentTime * 1000));
        }
    };
    const toggleMute = () => {
        if (videoRef.current) {
            videoRef.current.muted = !isMuted;
            setIsMuted(!isMuted);
        }
    };
    const togglePlay = () => {
        if (videoRef.current) {
            if (isPlaying) {
                videoRef.current.pause();
            } else {
                videoRef.current.play();
            }
            setIsPlaying(!isPlaying);
        }
    };

    const toggleFullscreen = () => {
        if (videoRef.current) {
            if (!isFullscreen) {
                videoRef.current.requestFullscreen();
            } else {
                document.exitFullscreen();
            }
            setIsFullscreen(!isFullscreen);
        }
    };
    const getTimeFromPosition = (clientX: number) => {
        if (!progressBarRef.current) return 0;
        const rect = progressBarRef.current.getBoundingClientRect();
        let x = clientX - rect.left;
        x = Math.max(0, Math.min(x, rect.width));
        const percent = x / rect.width;
        return percent * scenario.duration;
    };

    const handleMouseDown = (e: React.MouseEvent) => {
        setIsSeeking(true);
        const newTime = getTimeFromPosition(e.clientX);
        if (videoRef.current) {
            videoRef.current.currentTime = newTime / 1000;
            setCurrentTime(newTime);
        }
    };

    const handleMouseMove = (e: MouseEvent) => {
        if (!isSeeking) return;
        const newTime = getTimeFromPosition(e.clientX);
        if (videoRef.current) {
            videoRef.current.currentTime = newTime / 1000;
            setCurrentTime(newTime);
        }
    };

    const handleMouseUp = () => {
        setIsSeeking(false);
    };

    useEffect(() => {
        if (isSeeking) {
            window.addEventListener('mousemove', handleMouseMove);
            window.addEventListener('mouseup', handleMouseUp);
        } else {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        }
        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isSeeking]);
    const handleTouchStart = (e: React.TouchEvent) => {
        setIsSeeking(true);
        const touch = e.touches[0];
        const newTime = getTimeFromPosition(touch.clientX);
        if (videoRef.current) {
            videoRef.current.currentTime = newTime / 1000;
            setCurrentTime(newTime);
        }
    };

    const handleTouchMove = (e: TouchEvent) => {
        if (!isSeeking) return;
        const touch = e.touches[0];
        const newTime = getTimeFromPosition(touch.clientX);
        if (videoRef.current) {
            videoRef.current.currentTime = newTime / 1000;
            setCurrentTime(newTime);
        }
    };

    const handleTouchEnd = () => {
        setIsSeeking(false);
    };

    useEffect(() => {
        if (isSeeking) {
            window.addEventListener('touchmove', handleTouchMove);
            window.addEventListener('touchend', handleTouchEnd);
        } else {
            window.removeEventListener('touchmove', handleTouchMove);
            window.removeEventListener('touchend', handleTouchEnd);
        }
        return () => {
            window.removeEventListener('touchmove', handleTouchMove);
            window.removeEventListener('touchend', handleTouchEnd);
        };
    }, [isSeeking]);


    return (
        <div
            className="video-container"
            onMouseMove={handleUserActivity}
            onMouseDown={handleUserActivity}
            onTouchStart={handleUserActivity}
            onClick={handleContainerClick}
        >
            <video
                ref={videoRef}
                src={videoUrl}
                poster="/1.png"
                controls={false}
                onTimeUpdate={handleTimeUpdate}
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
            <div className={`controls-overlay${(!showControls || fadeOut) ? ' hide' : ''}`}>
                <>
                    <button
                        onClick={toggleMute}
                        className="mute-btn-top"
                        aria-label={isMuted ? "Unmute" : "Mute"}
                    >
                        {isMuted ? <VolumeOffIcon fontSize="inherit" /> : <VolumeUpIcon fontSize="inherit" />}
                    </button>
                    <button
                        className="big-play-btn"
                        onClick={togglePlay}
                        aria-label={isPlaying ? "Pause video" : "Play video"}
                    >
                        {isPlaying ? <PauseIcon fontSize="inherit" /> : <PlayArrowIcon fontSize="inherit" />}
                    </button>
                    <div
                        className="progress-bar-container"
                        ref={progressBarRef}
                        onMouseDown={handleMouseDown}
                        onTouchStart={handleTouchStart}
                    >
                        <div
                            className="progress-bar-fill"
                            style={{ width: `${(currentTime / scenario.duration) * 100}%` }}
                        />
                    </div>
                </>
            </div>
            <div className="stats-overlay">
                {/* ...статистика, как в предыдущем ответе... */}
            </div>
        </div>

    );
};

export default VideoPlayer;
